import { z } from 'zod';
import { featureDisabledResponseSchema, } from './helpers';
export { featureDisabledResponseSchema };
/**
 * Connection status values
 */
export const connectionStatusSchema = z.enum([
    'none',
    'pending',
    'active',
    'disconnected',
]);
/**
 * GitHub status response schema
 * Matches backend organization-github.ts githubStatusResponseSchema
 */
export const githubStatusResponseSchema = z.object({
    enabled: z.boolean(),
    connectionStatus: connectionStatusSchema,
    connected: z.boolean(),
    githubOrgName: z.string().optional(),
    githubAccountId: z.number().optional(),
    connectedAt: z.string().optional(),
    importedCount: z.number().optional(),
});
/**
 * GitHub connect response schema - when feature is enabled
 * Matches backend organization-github.ts githubConnectResponseSchema
 */
export const githubConnectResponseSchema = z.object({
    enabled: z.literal(true).optional(),
    installUrl: z.string(),
});
/**
 * GitHub connect response - union of enabled and disabled states
 */
export const githubConnectResultSchema = z.union([
    githubConnectResponseSchema,
    featureDisabledResponseSchema,
]);
/**
 * GitHub decode state request schema
 */
export const githubDecodeStateRequestSchema = z.object({
    state: z.string(),
});
/**
 * GitHub decode state response schema
 */
export const githubDecodeStateResponseSchema = z.object({
    organizationId: z.string(),
});
/**
 * GitHub complete installation request schema
 */
export const githubCompleteInstallationRequestSchema = z.object({
    installation_id: z.string(),
    state: z.string(),
    setup_action: z.string().nullish(),
});
/**
 * GitHub complete installation success response schema
 * Matches backend organization-github.ts completeInstallationSuccessSchema
 */
export const githubCompleteInstallationResponseSchema = z.object({
    enabled: z.literal(true).optional(),
    success: z.literal(true),
    githubOrgName: z.string(),
});
/**
 * GitHub complete installation result - union of success and disabled states
 */
export const githubCompleteInstallationResultSchema = z.union([
    githubCompleteInstallationResponseSchema,
    featureDisabledResponseSchema,
]);
/**
 * GitHub manual connect request schema
 * Used when admin provides an installation ID directly
 */
export const githubManualConnectRequestSchema = z.object({
    installation_id: z
        .string()
        .min(1, 'Installation ID is required')
        .max(10, 'Installation ID is too long')
        .regex(/^\d+$/, 'Installation ID must be numeric'),
});
/**
 * GitHub manual connect result - reuses complete installation result
 * Returns { success: true, githubOrgName } or { enabled: false }
 */
export const githubManualConnectResultSchema = githubCompleteInstallationResultSchema;
/**
 * GitHub disconnect success response schema
 */
export const githubDisconnectResponseSchema = z.object({
    enabled: z.literal(true).optional(),
    success: z.literal(true),
});
/**
 * GitHub disconnect result - union of success and disabled states
 */
export const githubDisconnectResultSchema = z.union([
    githubDisconnectResponseSchema,
    featureDisabledResponseSchema,
]);
/**
 * GitHub sync response schema
 * Matches backend organization-github.ts syncResponseSchema
 */
export const githubSyncResponseSchema = z.object({
    enabled: z.literal(true).optional(),
    success: z.boolean(),
    usersAdded: z.number(),
    usersRemoved: z.number(),
    errorMessage: z.string().optional(),
    alreadyRunning: z.boolean().optional(),
});
/**
 * GitHub sync result - union of success and disabled states
 */
export const githubSyncResultSchema = z.union([
    githubSyncResponseSchema,
    featureDisabledResponseSchema,
]);
/**
 * GitHub sync status response schema
 * Matches backend organization-github.ts syncStatusResponseSchema
 */
export const githubSyncStatusResponseSchema = z.object({
    enabled: z.boolean(),
    lastSyncAt: z.string().optional(),
    // Note: 'running' state is indicated by isRunning flag, not lastSyncStatus
    lastSyncStatus: z.enum(['completed', 'failed']).optional(),
    lastSyncType: z.enum(['manual', 'periodic', 'webhook']).optional(),
    isRunning: z.boolean(),
    importedCount: z.number(),
    linkedCount: z.number(),
});
// ============================================================================
// IMPORTED USERS
// ============================================================================
/**
 * Import record status values
 *
 * - imported: Discovered from provider sync, not yet linked to a Kirin user
 * - linked: User authenticated and linked to Kirin account
 * - removed_from_provider: User removed from provider org (record retained for re-association)
 */
export var ImportedUserStatus;
(function (ImportedUserStatus) {
    ImportedUserStatus["Imported"] = "imported";
    ImportedUserStatus["Linked"] = "linked";
    ImportedUserStatus["RemovedFromProvider"] = "removed_from_provider";
})(ImportedUserStatus || (ImportedUserStatus = {}));
export const importedUserStatusSchema = z.nativeEnum(ImportedUserStatus);
/**
 * GitHub user data as stored in providerUserData JSONB
 */
export const githubUserDataSchema = z.object({
    userId: z.number(),
    login: z.string(),
    email: z.string().nullish(),
    name: z.string().nullish(),
    avatarUrl: z.string().nullish(),
    /**
     * SSO/SAML email from the org's identity provider (for SSO-enabled orgs).
     * Stored for admin visibility on unmatched imported users — helps diagnose
     * why a GitHub user wasn't auto-linked (typo, wrong domain, user not in Kirin).
     * For linked users this is redundant with the Kirin user's email.
     */
    ssoEmail: z.string().nullish(),
});
/**
 * Imported user response schema
 * Represents a GitHub user synced to the organization
 */
export const githubImportedUserSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string(),
    provider: z.literal('github'),
    providerUserId: z.string(),
    providerUserData: githubUserDataSchema,
    status: importedUserStatusSchema,
    kirinUserId: z.string().uuid().nullish(),
    importedAt: z.string(),
    linkedAt: z.string().nullish(),
    removedFromProviderAt: z.string().nullish(),
});
/**
 * GitHub imported users list response schema
 */
export const githubImportedUsersResponseSchema = z.object({
    enabled: z.literal(true).optional(),
    users: z.array(githubImportedUserSchema),
    total: z.number(),
});
/**
 * GitHub imported users result - union of success and disabled states
 */
export const githubImportedUsersResultSchema = z.union([
    githubImportedUsersResponseSchema,
    featureDisabledResponseSchema,
]);
// ============================================================================
// ACCOUNT LINKING
// ============================================================================
/**
 * GitHub link initiate response schema
 * Returns the OAuth authorization URL to redirect the user to
 */
export const githubLinkInitiateResponseSchema = z.object({
    enabled: z.literal(true).optional(),
    redirectUrl: z.string(),
});
/**
 * GitHub link initiate result - union of success and disabled states
 */
export const githubLinkInitiateResultSchema = z.union([
    githubLinkInitiateResponseSchema,
    featureDisabledResponseSchema,
]);
/**
 * GitHub link callback request schema
 * Sent by frontend after OAuth callback from GitHub
 */
export const githubLinkCallbackRequestSchema = z.object({
    code: z.string(),
    state: z.string(),
});
/**
 * GitHub link callback success response schema
 */
export const githubLinkCallbackResponseSchema = z.object({
    enabled: z.literal(true).optional(),
    success: z.literal(true),
    providerLogin: z.string(),
});
/**
 * GitHub link callback result - union of success and disabled states
 */
export const githubLinkCallbackResultSchema = z.union([
    githubLinkCallbackResponseSchema,
    featureDisabledResponseSchema,
]);
/**
 * GitHub link status response schema
 * Returns whether the current user has linked their GitHub account
 */
export const githubLinkStatusResponseSchema = z.object({
    enabled: z.boolean(),
    isLinked: z.boolean(),
    providerUserId: z.string().optional(),
    providerLogin: z.string().optional(),
});
/**
 * GitHub unlink success response schema
 */
export const githubUnlinkResponseSchema = z.object({
    enabled: z.literal(true).optional(),
    success: z.literal(true),
});
/**
 * GitHub unlink result - union of success and disabled states
 */
export const githubUnlinkResultSchema = z.union([
    githubUnlinkResponseSchema,
    featureDisabledResponseSchema,
]);
//# sourceMappingURL=github.js.map